import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Lightbox } from "@/components/site/Lightbox";
import featured from "@/assets/gallery/256.jpg";
import project004 from "@/assets/gallery/004.webp";
import project004Mobile from "@/assets/home/004-card.avif";
import project261 from "@/assets/gallery/261.webp";
import project243 from "@/assets/gallery/243.jpg";
import project246 from "@/assets/gallery/246.webp";
import project246Mobile from "@/assets/home/246-m.webp";

const FEATURED = featured;
const PROJECTS: {
  img: string;
  mobile?: string;
  mobileType?: string;
  file: string;
  label: string;
}[] = [
  {
    img: project004,
    mobile: project004Mobile,
    mobileType: "image/avif",
    file: "004.webp",
    label: "Marble Master Shower",
  },
  { img: project261, file: "261.webp", label: "Soaking Tub Surround" },
  { img: project243, file: "243.jpg", label: "Marble Fireplace Surround" },
  {
    img: project246,
    mobile: project246Mobile,
    mobileType: "image/webp",
    file: "246.webp",
    label: "Patterned Mosaic Shower",
  },
];

export function Gallery() {
  const [photos, setPhotos] = useState<string[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  async function openAt(filename: string) {
    const [{ gallerySrc }, manifestMod] = await Promise.all([
      import("@/data/galleryAssets"),
      import("@/data/galleryManifest.json"),
    ]);
    const manifest = manifestMod.default as string[];
    const all = manifest.map((file) => gallerySrc(file));
    setPhotos(all);
    const index = all.indexOf(gallerySrc(filename));
    setLightboxIndex(index === -1 ? 0 : index);
  }
  return (
    <section id="gallery" className="py-20 md:py-28 bg-cream">
      <div className="container-prose">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <span className="eyebrow">The proof</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-foreground leading-tight">
              30 years of work. Every cut placed by hand.
            </h2>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
              Custom showers built to last decades — waterproofed properly, set on dead-flat
              substrates, with custom mosaics and niches detailed to the millimeter. This is
              signature RKD work.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="font-display text-2xl text-primary">300+</div>
                <div className="text-muted-foreground">Documented jobs</div>
              </div>
              <div>
                <div className="font-display text-2xl text-primary">30 yrs</div>
                <div className="text-muted-foreground">Behind every install</div>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => openAt("256.jpg")}
            className="group relative overflow-hidden bg-ink shadow-[var(--shadow-elegant)] aspect-[4/5] focus:outline-none focus:ring-2 focus:ring-primary cursor-zoom-in"
            aria-label="Open project slideshow"
          >
            <img
              src={FEATURED}
              alt="Diamond mosaic shower with custom niche"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          </button>
        </div>
        <div className="mt-20 grid sm:grid-cols-2 gap-6">
          {PROJECTS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => openAt(p.file)}
              className="group relative overflow-hidden bg-ink focus:outline-none focus:ring-2 focus:ring-primary cursor-zoom-in text-left"
              aria-label={`Open ${p.label} slideshow`}
            >
              <picture>
                {p.mobile ? (
                  <source
                    media="(max-width: 767px)"
                    srcSet={p.mobile}
                    type={p.mobileType ?? "image/webp"}
                  />
                ) : null}
                <img
                  src={p.img}
                  alt={p.label}
                  loading="lazy"
                  className="h-80 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </picture>
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-white text-sm uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                {p.label}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <Link
            to="/gallery"
            className="px-8 py-4 bg-primary text-primary-foreground text-sm uppercase tracking-widest hover:opacity-90 transition-opacity"
          >
            View Complete Full Gallery →
          </Link>
        </div>
      </div>
      {lightboxIndex !== null && photos && (
        <Lightbox
          images={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </section>
  );
}
